import React, { useState } from 'react'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Board } from '../campaign/components/board/board'
import logoLarge from '../assets/logo-large.png'
import { Dropdown, Flex, Layout } from 'antd'
import { PageHeader } from '../components/page-header'
import { useCampaign, useCampaigns } from '../campaign/hooks'
import { CandidateCreateModal } from '../candidate/components/candidate-create-modal'
import { CandidateImportModal } from '../candidate/components/candidate-import-modal'
import { CandidateArchiveDrawer } from '../candidate/components/candidate-archive-drawer'
import { Toolbar } from '../campaign/components/board/toolbar'
import { useTitle } from '../hooks/use-title'
import { SHOW_CAMPAIGN } from '../user/permission'

export const Route = createFileRoute('/_auth/campaigns/$campaignId/full-board')({
    validateSearch: z.object({
        candidateId: z.string().optional(),
        bulk: z.boolean().optional(),
        archive: z.boolean().optional(),
        create: z.boolean().optional(),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.can(SHOW_CAMPAIGN)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: RouteComponent,
})

export default function RouteComponent() {
    const { campaignId } = Route.useParams()
    const { candidateId, create, bulk, archive } = Route.useSearch()
    const campaign = useCampaign(campaignId)
    const campaigns = useCampaigns()
    const navigate = useNavigate()

    const [selected, setSelected] = useState<Record<string, boolean>>({})

    useTitle(campaign.data?.title)

    if (!campaign.isSuccess) {
        return null
    }

    function navigateSearch<R extends typeof Route, S extends R['types']['searchSchema']>(search: S) {
        return () => {
            navigate({
                to: '.',
                search: (previous) => ({
                    ...previous,
                    ...search,
                }),
            })
        }
    }

    return (
        <Layout
            className="p-4"
            style={{ height: '100vh' }}
        >
            <Flex
                vertical
                className="h-full"
                gap="large"
            >
                <Flex
                    gap="large"
                    className="lg:flex-row flex-col items-start lg:items-center"
                >
                    <Link to="/dashboard">
                        <img
                            alt="log"
                            width="169"
                            src={logoLarge}
                        />
                    </Link>
                    <PageHeader
                        title={
                            <Dropdown
                                menu={{
                                    items: campaigns.data?.data.map((campaign) => ({
                                        label: campaign.title,
                                        key: campaign.id,
                                    })),
                                    onClick: ({ key }) => {
                                        navigate({
                                            to: '.',
                                            params: {
                                                campaignId: key,
                                            },
                                        })
                                    },
                                }}
                            >
                                {campaign.data?.title}
                            </Dropdown>
                        }
                        suffix={
                            <Toolbar
                                fullscreen
                                campaign={campaign.data!}
                                selected={selected}
                                setSelected={setSelected}
                            />
                        }
                    />
                </Flex>

                <Flex
                    gap="middle"
                    vertical
                    className="h-full"
                >
                    <Board
                        style={{
                            height: '100%',
                            maxWidth: '100%',
                        }}
                        campaignId={campaignId}
                        height={1000}
                        columns={campaign.data.columns || []}
                        candidateId={candidateId}
                        selected={selected}
                        setSelected={setSelected}
                    />
                </Flex>
            </Flex>
            <CandidateCreateModal
                onCancel={navigateSearch({
                    create: undefined,
                })}
                open={create}
                campaignId={campaignId}
            />
            <CandidateImportModal
                onCancel={navigateSearch({
                    bulk: undefined,
                })}
                open={bulk}
                campaignId={campaignId}
            />
            <CandidateArchiveDrawer
                open={archive}
                onClose={navigateSearch({ archive: undefined })}
                campaignId={campaignId}
            />
        </Layout>
    )
}
